import type { ReactNode } from 'react';

const CONTROL =
  'w-full rounded-2xl border-0 bg-mint-50/70 px-5 py-3.5 text-ink ring-1 ring-mint-200 ' +
  'transition-shadow duration-200 placeholder:text-muted/60 focus:bg-white focus:ring-2 ' +
  'focus:ring-mint-500 focus:outline-none';

const CONTROL_INVALID = 'ring-2 ring-blush-600 bg-blush-50/60';

type FieldProps = {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: (props: { id: string; className: string; 'aria-describedby'?: string; 'aria-invalid'?: true }) => ReactNode;
};

export function Field({ id, label, required, error, hint, children }: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block font-display text-sm font-semibold text-ink">
        {label}
        {required && (
          <span className="text-blush-600" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>

      {children({
        id,
        className: `${CONTROL} ${error ? CONTROL_INVALID : ''}`,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true as const } : {}),
      })}

      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs leading-relaxed text-muted">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} className="mt-1.5 text-xs font-semibold text-blush-700">
          {error}
        </p>
      )}
    </div>
  );
}
